// Fallback fee labels; live values come from the API (fee_outlook / GET /fees/policy), which roll over each year.
export const FEE_YEAR = "2027/28";
export const UK_HOME_CAP_GBP = 10050;
export const UK_HOME_CAP_USD = 12764;
export const UK_FOUNDATION_YEAR_GBP = 9535;
export const UK_CAP_LABEL = "2027/28 (GOV.UK regulated cap)";
export const UCAS_FEE_LABEL = "£34.50 (UCAS 2027 entry)";
export const UCAS_FEE_URL = "https://www.ucas.com/faqs/what-is-the-application-fee-for-the-2027-cycle";
export const UK_CAP_SOURCE_URL = "https://www.gov.uk/government/publications/tuition-fees-and-student-support-2026-to-2027-academic-year/changes-to-tuition-fees-2026-to-2027-academic-year-and-2027-to-2028-academic-year";

export function intlFeeNote(intl, format) {
  if (!intl) return null;
  const est = intl.estimate_year || FEE_YEAR;
  if (intl.basis === "editorial_baseline") return `${est} estimate · editorial baseline`;
  if (intl.is_estimate) {
    const pub = format ? ` ${format(intl.published_usd)}` : "";
    return `${est} estimate · from ${intl.published_year} published fee${pub} + ${intl.growth_pct}%/yr`;
  }
  if (intl.published_year) return `${intl.published_year} published fee`;
  if (intl.source_academic_year) return `${intl.source_academic_year} fee (source year)`;
  return "Published fee · year not stated";
}

// Sourced reference: published tuition + separately listed required fees, counted once (no projection).
export function publishedSubtotalNote(intl, format) {
  if (!intl || intl.published_subtotal_usd == null) return null;
  const base = `${intl.published_subtotal_year || "Published"} tuition ${format(intl.published_usd)} + required fees ${format(intl.required_fees_usd)}`;
  return UNRESOLVED_FEES[intl.required_fees_status] ? `${base} (listed fees only · complete required fees not verified)` : base;
}

// Conditional charges (e.g. insurance unless waived) stay separate: never part of the published subtotal or any total.
export function conditionalFeeNote(f) {
  if (!f) return null;
  return [f.period, f.condition && `Condition: ${f.condition}`, "separate · not included in totals"].filter(Boolean).join(" · ");
}

const UNRESOLVED_FEES = {
  not_verified: "required fees not verified",
  conditional_on_load: "required fees depend on credit load",
};

// Reviewed-unknown required fees: any total is a partial subtotal, never a complete degree cost.
export function feesUnverifiedNote(intl) {
  const why = UNRESOLVED_FEES[intl?.required_fees_status];
  if (!why) return null;
  if (intl.required_fees_usd != null) return "Partial · tuition and known listed fees only · complete required fees not verified (additional fees unknown) · not a complete degree cost";
  return `Partial · tuition and listed course costs only · ${why} · not a complete degree cost`;
}

// A total built on projected tuition is an estimate: never present it as a verified/published total.
export function estimatedTotalNote(intl, { indicative = false } = {}) {
  if (indicative || !intl?.is_estimate) return null;
  return `Estimated · uses ${intl.estimate_year || FEE_YEAR} tuition estimate · not a university-published total`;
}

export function homeFeeNote(home) {
  if (!home) return null;
  if (home.basis === "devolved_nation") return home.label;
  const cap = home.label || UK_CAP_LABEL;
  if (home.basis === "foundation_year") return `Foundation year · ${cap}`;
  if (home.basis === "no_tef_award") return `${cap} · lower cap, no TEF award`;
  const fy = home.foundation_year_gbp ? ` · foundation year £${home.foundation_year_gbp.toLocaleString()}` : "";
  if (home.basis === "cap_tef_not_on_file") return `${cap} · maximum, TEF award not on file${fy}`;
  return `${cap}${fy}`;
}

export function trueCostTuitionNote(tc) {
  if (!tc || tc.tuition_usd == null) return null;
  if (tc.tuition_is_estimate) {
    const est = tc.tuition_year || FEE_YEAR;
    const ref = tc.tuition_published_subtotal_usd != null
      ? ` · ${tc.tuition_published_subtotal_year} published tuition + required fees $${tc.tuition_published_subtotal_usd.toLocaleString()}`
      : "";
    return tc.tuition_published_year
      ? `Tuition: ${est} estimate (from ${tc.tuition_published_year} published fee)${ref}`
      : `Tuition: ${est} estimate${ref}`;
  }
  if (tc.tuition_year) return `Tuition: ${tc.tuition_year} published fee`;
  return tc.tuition_source_academic_year
    ? `Tuition: ${tc.tuition_source_academic_year} fee (source year)`
    : "Tuition: published fee · year not stated";
}

// Extras outside the reviewed-plan engine's allowlist: its evidence never covers them (their own provenance is unknown).
export const EXTRA_KEYS = ["lab_studio_fees_usd", "equipment_software_usd", "books_materials_usd", "compulsory_travel_usd"];
export const INHERITED_EXTRA_NOTE = "Additional costs · provenance not verified for this fee reference · year unknown";
const REVIEWED_BASIS = new Set(["university_international_rate", "same_rate_all_students"]);
const isNum = (v) => Number.isFinite(v);

// Current cost evidence matches the reviewed-plan contract (year/basis/lineage); says nothing about which fields the last plan wrote.
export function reviewedTuitionRow(c) {
  const ev = c?.evidence;
  if (!ev || typeof ev !== "object") return false;
  const yr = ev.academic_year;
  return typeof ev.migration === "string" && ev.migration.startsWith("reviewed_") && "prior_observation" in ev
    && typeof yr === "string" && /^20\d\d-\d\d$/.test(yr)
    && c.tuition_academic_year === yr && c.tuition_intl_fee_year === yr
    && REVIEWED_BASIS.has(c.tuition_intl_basis) && isNum(c.tuition_intl_annual_usd)
    && typeof ev.url === "string" && /^https?:\/\//.test(ev.url);
}

// Non-zero extras on a reviewed-evidence row (not covered by that evidence); {} for every other row (legacy behaviour unchanged).
export function inheritedExtras(c) {
  if (!reviewedTuitionRow(c)) return {};
  return Object.fromEntries(EXTRA_KEYS.filter((k) => isNum(c[k]) && c[k] !== 0).map((k) => [k, c[k]]));
}

const FEES_UNRESOLVED_REASON = "Incomplete · complete required fees not verified (unknown, not $0) · no complete or comparable total";
const EXTRAS_UNVERIFIED_REASON = "Incomplete · additional costs whose provenance is not verified for this fee reference · no complete or comparable total";
const FEE_SCOPE_REASON = "Incomplete · whether this tuition figure covers the listed required fees is not confirmed · no comparable total";
const TUITION_MISSING_REASON = "Incomplete · tuition not on file (unknown, not $0) · no complete or comparable total";
export const FIRST_YEAR_PERIOD = "first academic year only";
const FIRST_YEAR_REASON = "Incomplete · first-academic-year rate only (later years not sourced) · no degree or comparable total";
export const FIRST_YEAR_TOTAL_LABEL = "Not available · first-year rate only";
/** Reviewed rate sourced for the first academic year/entering cohort only (mirrors backend first_year_only). */
export function firstYearOnly(c) {
  const p = c && typeof c === "object" ? c.tuition_period : null;
  return typeof p === "string" && p.startsWith(FIRST_YEAR_PERIOD);
}

// Shared annual reading for totals/rankings (mirrors backend known_annual_cost): tuition + listed required fees once + extras.
// Missing tuition, unresolved fees or unverified extras: no total (never 0 or tuition-only); explicit finite 0 stays valid;
// tuition + known listed fees stay an annual partial reference.
export function knownAnnualCost(c, tuition, courseLevel = true) {
  const cost = c && typeof c === "object" && !Array.isArray(c) ? c : {};
  const tu = isNum(tuition) ? tuition : null;
  const fees = isNum(cost.required_fees_annual_usd) && !cost.tuition_includes_required_fees ? cost.required_fees_annual_usd : 0;
  const unverified = inheritedExtras(cost);
  const extras = Object.keys(unverified).length ? 0 : EXTRA_KEYS.reduce((s, k) => s + (isNum(cost[k]) ? cost[k] : 0), 0);
  const reason = tu == null ? TUITION_MISSING_REASON
    : firstYearOnly(cost) ? FIRST_YEAR_REASON
    : UNRESOLVED_FEES[cost.required_fees_status] ? FEES_UNRESOLVED_REASON
    : Object.keys(unverified).length ? EXTRAS_UNVERIFIED_REASON
    : fees && !courseLevel ? FEE_SCOPE_REASON : null;
  const partial = reason != null && courseLevel && tu > 0 && fees > 0;
  const yr = cost.tuition_academic_year;
  return { tuition_usd: tu, required_fees_usd: fees, extras_usd: extras, complete: reason == null, incomplete_reason: reason,
    annual_total_usd: reason == null ? tu + fees + extras : null,
    partial_annual_reference_usd: partial ? tu + fees : null,
    reference_academic_year: partial && typeof yr === "string" && /^20\d\d-\d\d$/.test(yr) ? yr : null };
}

// Sourced annual reference only (explicit academic year; never multiplied by duration or projected).
export function partialAnnualReferenceNote(k, format) {
  if (!k || k.partial_annual_reference_usd == null) return null;
  const yr = k.reference_academic_year ? `${k.reference_academic_year.replace("-", "/")} ` : "";
  return `${format(k.partial_annual_reference_usd)} · ${yr}partial annual academic reference (tuition + known listed fees; not a complete cost)`;
}
